// Static configuration for the New Company Wizard V3.
// Plans, add-ons, durations, payment methods, color tokens, and the
// role-bundle mapping each plan grants on the backend.

export const COLORS = {
  navy: '#0f1847',
  navyDeep: '#070b24',
  navyDark: '#0d1340',
  navyMid: '#111b5e',
  gold: '#D4AF37',
  goldDark: '#B8960C',
  blue: '#1a3a8f',
  purple: '#8B6FC0',
  green: '#22a06b',
  border: '#e2e6f0',
  inputBg: '#fafbfc',
};

export const COMP_TYPE = {
  GLOBAL: 'global',
  ALPHA: 'alpha',
};

// ---------------------------------------------------------------------------
// Plans
// ---------------------------------------------------------------------------

// Global tiers, cheapest first, then Alpha (a separate product line that
// is deliberately NOT part of the Global ladder and is never re-priced or
// re-scoped alongside it).
//
// These ids replaced an older ladder: standard($300) / premium($450) /
// pro_elite($650). The tier NAMES moved down a rung -- what used to be
// "Premium" is now "Regular", and "Premium" now means the TOP tier -- so a
// stored plan string cannot be read at face value across that boundary.
// LEGACY_PLAN_NAMES below is the only correct way to interpret an old one.
export const PLAN_IDS = {
  BASIC: 'basic',
  REGULAR: 'regular',
  PREMIUM: 'premium',
  ALPHA: 'alpha',
};

// Old planName -> new planName, for reading a company saved before the
// rename. 'Pro' was an alias for 'Pro Elite'.
//
// "Premium" is deliberately NOT mapped, even though the old middle tier
// was called that. The string is ambiguous — it names the pre-rename
// MIDDLE tier and the post-rename TOP tier — and nothing in the name can
// tell them apart. Mapping it meant every company legitimately on the new
// Premium was read as the old middle tier: the Manage Company wizard
// opened them on "Regular", and saving would have written that downgrade
// back.
//
// Resolving it as the CURRENT tier is the correct default because the
// startup migration renames every pinned company before the app serves a
// single request, so a stored "Premium" that reaches this map is a
// post-rename one. The migration keeps its own list of pre-rename names
// (see BootStrap) precisely because it needs the other reading.
export const LEGACY_PLAN_NAMES = {
  Standard: 'Basic',
  'Pro Elite': 'Premium',
  Pro: 'Premium',
};

// Plan names an operator has renamed AWAY from, filled in by
// planCatalogRuntime when the live rate card loads (old name -> current
// name). Kept separate from LEGACY_PLAN_NAMES: that map is history the code
// knows about, this one is data somebody produced this afternoon, and a
// rename made today is the more recent truth about the string.
export const CATALOG_ALIASES = {};

// Read a stored Company.planName as a CURRENT tier name. Every plan-name
// comparison must go through this, not through the raw string: until the
// startup migration has run, live companies still carry the old names, and
// a raw `=== 'Premium'` both misses a legacy top-tier company and matches
// a legacy middle-tier one. Returns '' for a missing plan.
export function normalizePlanName(planName) {
  const raw = String(planName || '').trim();
  if (!raw) return '';
  return CATALOG_ALIASES[raw] || LEGACY_PLAN_NAMES[raw] || raw;
}

/** The plan a stored Company.planName refers to, or null. */
export function planByName(planName) {
  const name = normalizePlanName(planName);
  if (!name) return null;
  const key = name.toLowerCase();
  return (
    Object.values(PLANS).find(
      (p) => String(p.name || '').toLowerCase() === key
    ) || null
  );
}

export const PLANS = {
  [PLAN_IDS.BASIC]: {
    id: PLAN_IDS.BASIC,
    name: 'Basic',
    price: 250,
    color: COLORS.purple,
    badge: null,
    sections: [
      {
        // The entry tier is the one card where naming the eight screens
        // earns its length: a buyer here is deciding whether the product
        // does what they need at all, not how the tiers differ. Labels
        // match PlanCatalogDefaults.FEATURES so the shipped card and the
        // live one read identically. See SECTION_OVERRIDES in
        // planCatalogRuntime.js for the tiers that collapse this instead.
        title: 'Control Center',
        color: COLORS.purple,
        items: [
          'Dashboard',
          'Customers',
          'Transactions',
          'Ticket Out',
          'Bonus',
          'Raffles',
          'Notes',
          'Employee Clock In / Out',
        ],
      },
      {
        title: 'Readings',
        color: COLORS.purple,
        items: ['Reading Sessions', 'Record Reading Sessions'],
      },
      {
        // Employee Sessions lives here, not under Readings, so the same
        // feature sits in the same section on every tier — Regular and
        // Premium both present it under Operations Monitor.
        title: 'Operations Monitor',
        color: COLORS.purple,
        items: ['Employee Sessions'],
      },
      {
        title: 'Accounting',
        color: COLORS.purple,
        items: ['Basic Weekly Report'],
      },
    ],
  },
  [PLAN_IDS.REGULAR]: {
    id: PLAN_IDS.REGULAR,
    name: 'Regular',
    price: 450,
    color: COLORS.blue,
    badge: 'POPULAR',
    sections: [
      {
        title: 'Control Center',
        color: COLORS.blue,
        note: 'All Features Included',
      },
      {
        title: 'Readings',
        color: COLORS.blue,
        note: 'All Features Included',
      },
      {
        title: 'Operations Monitor',
        color: COLORS.blue,
        items: ['Point Watching', 'Employee Sessions'],
      },
      {
        title: 'Accounting',
        color: COLORS.blue,
        items: ['Advanced Weekly Report', 'Black List', 'Match Privilege'],
      },
      {
        title: 'Messaging',
        color: COLORS.blue,
        items: ['NuVe Boost (SMS/MMS Marketing)'],
      },
      {
        title: 'Extras',
        color: COLORS.blue,
        items: ['Link Accounts'],
      },
    ],
  },
  [PLAN_IDS.PREMIUM]: {
    id: PLAN_IDS.PREMIUM,
    name: 'Premium',
    price: 550,
    color: COLORS.goldDark,
    badge: null,
    sections: [
      {
        title: 'Control Center',
        color: COLORS.goldDark,
        note: 'All Features Included',
      },
      {
        title: 'Readings',
        color: COLORS.goldDark,
        note: 'All Features Included',
      },
      {
        // Collapsed, unlike Regular's, which names its two screens: by the
        // time a buyer is reading the top tier the point is that nothing is
        // missing. The row still has to exist — the Compare Plans table
        // decides a category by looking for a section with that title, so
        // dropping it would render "—" for Premium while Regular showed a
        // tick, the top tier appearing to lack what the middle tier has.
        title: 'Operations Monitor',
        color: COLORS.goldDark,
        note: 'All Features Included',
      },
      {
        title: 'Accounting',
        color: COLORS.goldDark,
        note: 'All Features Included',
      },
      {
        title: 'Messaging',
        color: COLORS.goldDark,
        items: ['NuVe Boost (SMS/MMS Marketing)'],
      },
      {
        // Extras is the one category that is always spelled out, never
        // collapsed to "All Features Included" — see sectionsFor() in
        // planCatalogRuntime.js. These are the discrete modules a buyer is
        // choosing a tier over, so naming them is the point; the shorthand
        // hides exactly what they came to check.
        //
        // The Accounting / Messaging features that used to be listed here
        // are covered by those sections' own rows above.
        title: 'Extras',
        color: COLORS.goldDark,
        items: ['NFC Tags for Ticket Out', 'Customer Mobile', 'Link Accounts'],
      },
    ],
  },
  [PLAN_IDS.ALPHA]: {
    id: PLAN_IDS.ALPHA,
    name: 'Alpha',
    price: 250,
    color: COLORS.gold,
    badge: null,
    sections: [
      {
        title: 'Control Center',
        color: COLORS.gold,
        note: 'All Features Included',
      },
      { title: 'Readings', color: COLORS.gold, note: 'All Features Included' },
      {
        title: 'Accounting',
        color: COLORS.gold,
        note: 'All Features Included',
      },
      {
        title: 'Extras',
        color: COLORS.gold,
        items: ['NFC Tags for Ticket Out', 'Customer Mobile', 'Link Accounts'],
      },
      {
        title: 'All Add-Ons',
        color: COLORS.gold,
        items: [
          'Customer Mobile',
          'Reading Machine Picture',
          'Security Dashboard',
          'Link Accounts',
        ],
      },
    ],
  },
};

// Every Global-lineup plan, cheapest first — what the internal Create
// Company wizard offers. Rewritten in place by planCatalogRuntime when the
// live rate card loads.
export const GLOBAL_PLAN_ORDER = [
  PLAN_IDS.BASIC,
  PLAN_IDS.REGULAR,
  PLAN_IDS.PREMIUM,
];

// The subset of GLOBAL_PLAN_ORDER a visitor may actually buy: active AND
// marked visible on the public site. The landing page and the sign-up wizard
// read this one, so withdrawing a plan from sale stops advertising it without
// breaking the back office, where a company still ON it has to stay editable.
export const PUBLIC_PLAN_ORDER = [
  PLAN_IDS.BASIC,
  PLAN_IDS.REGULAR,
  PLAN_IDS.PREMIUM,
];

// planId -> the feature ids ticked on it, and planId -> the client-side
// capability keys those features imply (constants/planFeatures.js reads the
// second one). Both are empty until the live catalog loads; the shipped
// PLAN_ROLES below is what answers before that.
export const PLAN_FEATURES = {};
export const PLAN_CAPABILITIES = {};

// ---------------------------------------------------------------------------
// Add-ons (Global Company only)
// ---------------------------------------------------------------------------

// Reading Machine Picture and Security Dashboard are no longer sold to
// Global companies -- they are Alpha capabilities now. They stay in this
// list, flagged `global: false`, rather than being deleted: a company
// that bought one before the change still has the id in Company.addOns,
// and dropping the entry would silently zero its price line and strip the
// roles ADD_ON_ROLES grants it. `getAvailableAddOns` hides them from the
// Global picker, which is what "remove from Global" actually needs.
export const ADD_ONS = [
  { id: 'mobile_match', name: 'Customer Mobile', price: 25, global: true },
  {
    id: 'reading_pictures',
    name: 'Reading Machine Picture',
    price: 150,
    global: false,
  },
  {
    id: 'security_dashboard',
    name: 'Security Dashboard',
    price: 150,
    global: false,
  },
  { id: 'link_accounts', name: 'Link Accounts', price: 50, global: true },
];

export const ADD_ON_BY_ID = ADD_ONS.reduce((acc, a) => {
  acc[a.id] = a;
  return acc;
}, {});

// ---------------------------------------------------------------------------
// License durations
// ---------------------------------------------------------------------------

export const DURATIONS = [
  { id: '1', label: '1 Month', months: 1, discount: 0, badge: null },
  {
    id: '3',
    label: '3 Months',
    months: 3,
    discount: 0.05,
    badge: 'Save 5%',
    badgeColor: '#5b6b8a',
  },
  {
    id: '6',
    label: '6 Months',
    months: 6,
    discount: 0.1,
    badge: 'Save 10%',
    badgeColor: COLORS.green,
  },
  {
    id: '12',
    label: '1 Year',
    months: 12,
    discount: 0.15,
    badge: 'Best Value',
    badgeColor: COLORS.gold,
  },
];

export const DURATION_BY_ID = DURATIONS.reduce((acc, d) => {
  acc[d.id] = d;
  return acc;
}, {});

// Terms a customer may CHOOSE. DURATIONS itself keeps withdrawn terms so a
// company already billed on one still resolves its months and discount —
// only the pickers narrow to what is on sale.
export function sellableDurations() {
  return DURATIONS.filter((d) => d.active !== false);
}

// ---------------------------------------------------------------------------
// Payment methods
// ---------------------------------------------------------------------------

export const PAYMENT_METHODS = {
  CASH_ZELLE: 'cash_zelle',
  CARD: 'card',
  BANK: 'bank',
  PENDING: 'pending',
};

export const PAYMENT_METHOD_LIST = [
  {
    id: PAYMENT_METHODS.PENDING,
    label: 'Collect Later',
    color: COLORS.purple,
    badge: 'PENDING',
  },
  {
    id: PAYMENT_METHODS.CASH_ZELLE,
    label: 'Paid Already',
    color: COLORS.gold,
    badge: 'PAID',
  },
];

// ---------------------------------------------------------------------------
// Plan -> backend role bundle
// ---------------------------------------------------------------------------

// Plan names that include NuVe Boost messaging. Mirrors
// BoostEligibility.PLANS_WITH_BOOST on the server -- keep the two in step.
// Matched on the stored Company.planName, not a plan id, because that is
// what the company document carries. Post-rename names only: a company
// still on a legacy name is migrated at startup before this is consulted.
const PLANS_WITH_BOOST = new Set(['Regular', 'Premium', 'Alpha']);

// Whether a stored plan name includes messaging. Reads the plan's actual
// role bundle so that ticking NuVe Boost off Regular in Plans & Pricing
// really does take it away, rather than leaving a hardcoded name list
// insisting otherwise. Falls back to the shipped names before the live
// catalog has loaded.
export function planIncludesBoost(planName) {
  const plan = planByName(planName);
  if (plan && PLAN_ROLES[plan.id]) {
    return PLAN_ROLES[plan.id].includes('ROLE_BOOST');
  }
  return PLANS_WITH_BOOST.has(normalizePlanName(planName));
}

// Settings access is mandatory for every package — without these the
// company has no way to manage rules, machines, games, bonuses, or
// employees. They are intentionally NOT optional add-ons.
//
// ROLE_MAINTENANCE_PERMIT is mandatory for the same reason: it is how a
// site grants and revokes an NDS tech admin temporary access to itself,
// not something it buys. Mirrors MANDATORY_SETTINGS_ROLES in
// CompanyService — keep the two lists in step.
const BASE_SETTINGS_ROLES = [
  'ROLE_SETTINGS',
  'ROLE_RULES',
  'ROLE_MACHINE',
  'ROLE_GAME',
  'ROLE_ASSIGN_MACHINE',
  'ROLE_MANAGE_BONUS',
  'ROLE_MANAGE_USERS',
  'ROLE_MAINTENANCE_PERMIT',
];

const BASIC_ROLES = [
  ...BASE_SETTINGS_ROLES,
  'ROLE_DASHBOARD',
  'ROLE_TRANSACTION',
  'ROLE_CUSTOMER',
  // Customer phone-number access rides along with Customers on every plan.
  // It is a per-EMPLOYEE toggle in the ManageUsers wizard (a child of
  // ROLE_CUSTOMER there), so leaving it out of the company pool made that
  // toggle ungrantable rather than making it exclusive.
  'ROLE_CUSTOMER_PRIVILEGIE',
  'ROLE_TICKET_OUT',
  'ROLE_BONUS',
  'ROLE_RAFFLE',
  // Raffle mobile access is a companion to Raffle (not a paid add-on), so
  // it's available on every plan that includes raffles.
  'ROLE_RAFFLE_MOBILE',
  'ROLE_NOTE',
  'ROLE_CHECK_IN_OUT',
  'ROLE_EMPLOYEE_SESSION',
  'ROLE_READING',
  'ROLE_MASTER_READING',
  'ROLE_ADD_MACHINE_READING',
  'ROLE_OPEN_LAST_TWO_READINGS',
  // Basic buys the Weekly Report, but a BASIC version of it: Cash Flow,
  // Expenses, Closing Balance, Readings (without the machine-type roll-up)
  // and two headline percentages -- no Manager Loan, no Daily Analysis, no
  // audit log. That trimming lives in constants/planFeatures.js, which
  // reads Company.planName off userInfo; these two roles only put the
  // screen in the sidebar. The standalone loan / short / Zelle reports
  // stay Premium, so ROLE_ACCOUNTING opens a group with one entry in it.
  'ROLE_ACCOUNTING',
  'ROLE_REPORT',
];

const REGULAR_ROLES = [
  ...BASIC_ROLES,
  'ROLE_POINT_WATCHING',
  // Link Accounts is bundled into Regular -- it is no longer something a
  // Regular company buys on top.
  'ROLE_LINK_ACCOUNTS',
  // ROLE_ACCOUNTING / ROLE_REPORT come up from BASIC_ROLES. What Regular
  // adds is the FULL report -- every tab plus the audit log -- and that is
  // a plan-name check in constants/planFeatures.js, not a role.
  // NuVe Boost (SMS/MMS messaging) — included in every plan except
  // Basic. Spreads into PREMIUM_ROLES / ALPHA_ROLES below. The backend
  // (CompanyService + BootStrap) enforces the same plan rule server-side,
  // so this is for wizard display parity.
  'ROLE_BOOST',
];

// Premium is the top GLOBAL tier: everything Global sells. Note what is
// deliberately absent, because "all the features" means all the features a
// Global company can buy, not every role in the system:
//
//   ROLE_MACHINE_PICTURE  - Reading Machine Picture is Alpha-only now.
//   ROLE_SECURITY / ROLE_CUSTOMER_WALLET_PASS - Security Dashboard, ditto.
//   ROLE_ATM_TRANSACTION  - Alpha-only; gates the Manager Summary chip and
//                           Manager Settings in the Report view.
//   ROLE_CUSTOMER_PRIVILEGIE is NOT in this list: customer phone access is
//                           on every plan (see BASIC_ROLES) — it is an
//                           employee-level toggle, not a tier feature.
const PREMIUM_ROLES = [
  ...REGULAR_ROLES,
  'ROLE_TICKET_NFC',
  'ROLE_CUSTOMER_LOAN_REPORT',
  'ROLE_EMPLOYEE_SHORT_REPORT',
  'ROLE_ZELLE_REPORT',
  'ROLE_OPEN_ALL_WEEKLY_REPORT',
  'ROLE_CUSTOMER_MOBILE',
];

// Alpha is a separate product line and is NOT the Global ladder plus
// extras. It keeps every capability it had before the Global rename --
// including the two that Global no longer sells -- so it is spelled out in
// full rather than spread from a Global tier. Do not re-point this at
// PREMIUM_ROLES: that is exactly how removing something from Global would
// silently remove it from Alpha too.
const ALPHA_ROLES = [
  ...REGULAR_ROLES,
  'ROLE_TICKET_NFC',
  'ROLE_CUSTOMER_LOAN_REPORT',
  'ROLE_EMPLOYEE_SHORT_REPORT',
  'ROLE_ZELLE_REPORT',
  'ROLE_OPEN_ALL_WEEKLY_REPORT',
  'ROLE_MACHINE_PICTURE',
  'ROLE_SECURITY',
  'ROLE_CUSTOMER_WALLET_PASS',
  'ROLE_CUSTOMER_MOBILE',
  'ROLE_ATM_TRANSACTION',
];

export const PLAN_ROLES = {
  [PLAN_IDS.BASIC]: BASIC_ROLES,
  [PLAN_IDS.REGULAR]: REGULAR_ROLES,
  [PLAN_IDS.PREMIUM]: PREMIUM_ROLES,
  [PLAN_IDS.ALPHA]: ALPHA_ROLES,
};

// Per-add-on extra roles (granted on top of the plan)
export const ADD_ON_ROLES = {
  mobile_match: ['ROLE_CUSTOMER_MOBILE'],
  reading_pictures: ['ROLE_MACHINE_PICTURE'],
  security_dashboard: ['ROLE_SECURITY', 'ROLE_CUSTOMER_WALLET_PASS'],
  link_accounts: ['ROLE_LINK_ACCOUNTS'],
};

// True when every role granted by the add-on is already in the plan's
// role bundle. Such add-ons should be hidden from the picker because
// the plan already covers them.
export function isAddOnIncludedInPlan(addOnId, planId) {
  const planRoles = new Set(PLAN_ROLES[planId] || []);
  const addOnRoles = ADD_ON_ROLES[addOnId] || [];
  if (addOnRoles.length === 0) return false;
  return addOnRoles.every((r) => planRoles.has(r));
}

// Add-ons available for purchase under the given plan: the configured
// add-ons minus any whose features the plan already includes.
export function getAvailableAddOns(planId) {
  const alpha = isAlphaPlan(planId);
  return ADD_ONS.filter(
    (a) =>
      a.active !== false &&
      (alpha || a.global !== false) &&
      !isAddOnIncludedInPlan(a.id, planId)
  );
}

// Alpha is a lineup, not an id. Published plans carry `lineup`, so ask that
// first and only fall back to the shipped id — a catalog can add a second
// Alpha-lineup plan, and hardcoding the id would price it as Global (with
// multi-month discounts Alpha deliberately doesn't get).
export function isAlphaPlan(planId) {
  const plan = PLANS[planId];
  if (plan && plan.lineup) return plan.lineup === 'alpha';
  return planId === PLAN_IDS.ALPHA;
}

// "Link accounts" is active for a company when the plan already includes
// it (Regular, Premium, Alpha) or when the add-on is selected.
export function hasLinkAccounts(planId, addOnIds) {
  if (isAddOnIncludedInPlan('link_accounts', planId)) return true;
  return Array.isArray(addOnIds) && addOnIds.includes('link_accounts');
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function generateLicenseKeyHex() {
  let out = '';
  for (let i = 0; i < 16; i += 1) {
    out += Math.floor(Math.random() * 16)
      .toString(16)
      .toUpperCase();
  }
  return out;
}

export function generateCompanyId() {
  const n = Math.floor(Math.random() * 50) + 1;
  return String(n).padStart(3, '0');
}

// Every new company is entitled to this many days of free trial. Billing
// always lands on the 15th, so the trial LICENSE stops there — which almost
// never comes to exactly this many days. computeTrialAdjustment settles the
// difference in dollars on the first invoice, so the entitlement is the same
// whichever day of the month they sign up. Mirrors CompanyService.TRIAL_DAYS
// — the server is authoritative for what's actually applied.
export const TRIAL_DAYS = 30;

// Live values, replaced in place by planCatalogRuntime when the rate card
// loads (both are editable in Plans & Pricing). The constants above are the
// shipped defaults and the fallback; read through the two helpers below so
// an edited trial length reaches the wizard's preview.
export const CATALOG_SETTINGS = {
  trialDays: TRIAL_DAYS,
  trialMinWindowDays: 10,
};

export function trialDays() {
  const v = Number(CATALOG_SETTINGS.trialDays);
  return v > 0 ? v : TRIAL_DAYS;
}

export function trialMinWindowDays() {
  const v = Number(CATALOG_SETTINGS.trialMinWindowDays);
  return v >= 0 ? v : TRIAL_MIN_WINDOW_DAYS;
}

// A trial shorter than this is no trial at all — the customer would be
// invoiced days after signing up — so the trial rolls to the following 15th
// instead. Mirrors CompanyService.TRIAL_MIN_WINDOW_DAYS.
export const TRIAL_MIN_WINDOW_DAYS = 10;

// Trial dates.
// - Trial ends on the NEXT 15th. Everyone in the system is charged on a
//   15th, so a new company shares that anchor from day one — this just
//   takes the first one it reaches: sign up Aug 1 and the trial ends
//   Aug 15, sign up Aug 28 and it ends Sep 15. Always ONE month whatever
//   paid term was selected: the trial is a month of value, not a quarter's.
// - Except when that 15th is under TRIAL_MIN_WINDOW_DAYS away, which isn't
//   a trial so much as an invoice landing days after signup — those roll
//   to the following 15th (Aug 10 → Sep 15, not Aug 15). Signing up ON the
//   15th rolls too, rather than expiring the same day.
// - Trial days therefore run ~10 to ~41 depending on signup date, and
//   computeTrialAdjustment settles the difference from TRIAL_DAYS on the
//   first invoice — a credit when short, an extra charge when the roll
//   pushed them over.
// - License expiry = trial end + the selected duration.
//
// The server recomputes both dates on create and is authoritative — these
// drive the wizard's preview.
export function computeTrialDates(now, durationMonths) {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const msPerDay = 24 * 60 * 60 * 1000;

  let target = new Date(today.getFullYear(), today.getMonth(), 15);
  if (target <= today) {
    target = new Date(today.getFullYear(), today.getMonth() + 1, 15);
  }
  if (Math.round((target - today) / msPerDay) < trialMinWindowDays()) {
    target = new Date(target.getFullYear(), target.getMonth() + 1, 15);
  }
  const diffDays = Math.round((target - today) / msPerDay);

  const expiry = new Date(target);
  expiry.setMonth(expiry.getMonth() + Number(durationMonths || 1));

  return {
    startDate: today,
    trialEnd: target,
    trialDays: diffDays,
    expiryDate: expiry,
  };
}

// Signed settlement between the trial actually given and the TRIAL_DAYS
// entitlement, in dollars, priced at monthly / TRIAL_DAYS per day.
// Negative = credit off the first invoice; positive = extra days to charge.
//
//   $650/mo starting 28 Aug bills 15 Sep: 18 days used, 12 short,
//     -12 × $21.67 = -$260 → first invoice $390.
//   $650/mo starting 10 Aug rolls to 15 Sep: 36 days used, 6 over,
//     +6 × $21.67 = +$130 → first invoice $780.
//
// Kept in sync with CompanyService.trialAdjustmentFor.
// `daysGiven` — the trial this company actually got. Named apart from the
// trialDays() entitlement helper below it, which the parameter used to
// shadow.
export function computeTrialAdjustment(monthlyRate, daysGiven) {
  const monthly = Number(monthlyRate) || 0;
  const entitlement = trialDays();
  const delta = (Number(daysGiven) || 0) - entitlement;
  if (monthly <= 0 || delta === 0) return 0;
  return Math.round(((monthly * delta) / entitlement) * 100) / 100;
}

// Renewal / non-trial expiry — mirrors the backend
// CompanyService.getNextCycleEndDate(now, months) (CompanyService.groovy).
// The calendar year is bucketed into `months`-sized blocks and expiry falls
// on the 15th of the block AFTER the one containing `now`. This is the exact
// value renewLicenseKey/createLicenseKey persists on the LicenseKey, so the
// wizard's displayed expiry matches what's stored (and what the profile
// shows). Trial creates use computeTrialDates instead (trialEnd + duration).
export function computeRenewalExpiry(now, durationMonths) {
  const base = new Date(now);
  const months = Number(durationMonths) || 1;
  const currentMonth = base.getMonth() + 1; // 1-12
  const startMonth = Math.floor((currentMonth - 1) / months) * months + 1;
  let nextStartMonth = startMonth + months;
  let year = base.getFullYear();
  if (nextStartMonth > 12) {
    nextStartMonth -= 12;
    year += 1;
  }
  // 15th of the expiry month. Built at local midnight (same convention as
  // computeTrialDates) so the ISO round-trip through formatDate keeps the
  // calendar date stable.
  return new Date(year, nextStartMonth - 1, 15);
}

export function formatDate(d) {
  if (!d) return '';
  const date = new Date(d);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function computeAuthorities(planId, addOnIds) {
  const set = new Set(PLAN_ROLES[planId] || []);
  (addOnIds || []).forEach((id) => {
    (ADD_ON_ROLES[id] || []).forEach((r) => set.add(r));
  });
  return Array.from(set);
}

// Best-effort match: a stored `planName` string -> wizard plan id.
// Mirrors the inference used by CompanyWizardV3 for legacy rows that
// pre-date the explicit planId field.
export function inferPlanIdFromName(planName) {
  if (!planName) return PLAN_IDS.REGULAR;
  const raw = String(planName).trim();
  // Resolve a pre-rename name FIRST. "Premium" is the trap: before the
  // rename it meant the $450 middle tier, after it means the $550 top one.
  // A company saved as "Premium" that has not been migrated yet must land
  // on Regular, not be promoted a tier for free.
  const name = LEGACY_PLAN_NAMES[raw] || raw;
  const lower = name.toLowerCase();
  if (lower.includes('alpha')) return PLAN_IDS.ALPHA;
  if (lower.includes('premium')) return PLAN_IDS.PREMIUM;
  if (lower.includes('basic')) return PLAN_IDS.BASIC;
  return PLAN_IDS.REGULAR;
}

// Returns the role set a company is entitled to based on its stored
// authorities (if present) or, as a fallback, its planName + addOns.
// Used by the Employee Management wizard to constrain which permissions
// an admin can grant — you cannot grant a feature the company hasn't
// paid for.
//
// BASE_SETTINGS_ROLES are always unioned in to mirror the backend's
// mandatory-settings injection in CompanyService — without this,
// legacy companies that haven't yet been re-saved would appear to
// lack Settings access in the wizard.
export function getCompanyAllowedAuthorities(company) {
  if (!company) return new Set();
  const out = new Set(BASE_SETTINGS_ROLES);

  const rawAuths = company.authorities;
  if (Array.isArray(rawAuths) && rawAuths.length) {
    rawAuths.forEach((a) => {
      const name = typeof a === 'string' ? a : a && a.authority;
      if (name) out.add(name);
    });
  } else if (company.planName) {
    const planId = inferPlanIdFromName(company.planName);
    computeAuthorities(planId, company.addOns || []).forEach((r) => out.add(r));
  }

  // Raffle mobile access rides along with the Raffle feature — it isn't a
  // separately-sold role. Derive it here so existing companies (whose
  // stored authorities predate this role) can still grant it without
  // needing the company to be re-saved.
  if (out.has('ROLE_RAFFLE')) {
    out.add('ROLE_RAFFLE_MOBILE');
  }

  // Customer phone-number access rides with Customers the same way, and is
  // derived here for the same reason: existing companies were saved when it
  // was Alpha-only, so their stored authorities don't carry it yet. Without
  // this the "Customer Phone Number Access" toggle stays greyed out on every
  // Global company until someone re-saves the company.
  if (out.has('ROLE_CUSTOMER')) {
    out.add('ROLE_CUSTOMER_PRIVILEGIE');
  }

  // NuVe Boost follows the PLAN, not the stored authority list. The
  // server (CompanyService + the BootStrap company-pool reconciliation)
  // puts ROLE_BOOST in the pool for every plan except Basic, but a
  // company document only picks it up on a re-save or a reboot — until
  // then the Employee wizard filtered the Boost row out of the Manager /
  // Owner / Tech Admin cards on a Regular, Premium or Alpha company
  // that plainly pays for messaging. Derived from planName here so the
  // wizard shows what the plan actually includes. Basic stays excluded,
  // so the row is still hidden where Boost isn't sold. Normalized first
  // so a company still on a pre-rename name is read as the tier it
  // bought rather than falling through to "no Boost".
  if (company.planName && planIncludesBoost(company.planName)) {
    out.add('ROLE_BOOST');
  }

  return out;
}

// Pricing rules per spec section 3.10.
// Alpha companies pay the flat monthly rate × months with NO duration
// discount (250 ×1 / ×3 / ×6 / ×12); global plans keep the 5/10/15%
// multi-month discounts. Mirrors CompanyService.cycleAmountFor.
export function computePricing({ planId, addOnIds, durationId }) {
  const plan = PLANS[planId];
  const duration = DURATION_BY_ID[durationId] || DURATIONS[0];
  if (!plan) {
    return {
      monthly: 0,
      gross: 0,
      discount: 0,
      total: 0,
      months: duration.months,
    };
  }
  // Skip anything the plan already bundles. A company that bought
  // Customer Mobile under the old top tier still carries 'mobile_match',
  // and the current Premium includes it — billing it again would charge
  // twice for one feature, and would make this quote disagree with
  // CompanyService.planWithAddOnsRate, which does the same skip.
  //
  // GLOBAL tiers only. Alpha bundles all four capabilities, so applying
  // this there would zero out every add-on line and quietly re-price a
  // product line that is not part of this change. CompanyService's
  // PLAN_INCLUDED_ADD_ONS omits Alpha for the same reason — keep the two
  // in step.
  const bundlesAddOns = plan.bundlesAddOns !== false && !isAlphaPlan(planId);
  const addOnTotal = (addOnIds || []).reduce((sum, id) => {
    if (bundlesAddOns && isAddOnIncludedInPlan(id, planId)) return sum;
    const a = ADD_ON_BY_ID[id];
    return sum + (a ? a.price : 0);
  }, 0);
  const monthly = plan.price + addOnTotal;
  const gross = monthly * duration.months;
  const discountPct = isAlphaPlan(planId) ? 0 : duration.discount;
  const discount = Math.round(gross * discountPct);
  const total = gross - discount;
  return {
    monthly,
    gross,
    discount,
    total,
    months: duration.months,
    discountPct,
  };
}
